import { styled } from '@linaria/react';
import { useState } from 'react';
import type { AgentMinerChildSession, AgentMinerTurn } from '../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDark,
  inkLight,
  purpleDarkest,
  purpleLightest,
  redBase,
  redDarkest,
  redLightest,
  secondaryBase,
  secondaryDarkest,
  secondaryLightest,
  skyLight,
  skyLightest,
} from '../../../styles/design-tokens';
import { EventBlock } from './event-block';
import { fmtDuration, fmtNum } from './formatters';
import { EffortBadge, ModelBadge } from './model-effort-badges';

type TurnCardProps = {
  turn: AgentMinerTurn;
  index: number;
  total: number;
  defaultOpen?: boolean;
  onOpen?: () => void;
  // Subagent child sessions keyed by id. When one of this turn's events carries
  // a matching `child_session`, the child session is rendered nested in place of
  // the flat subagent event block.
  childSessions?: Map<string, AgentMinerChildSession>;
};

/**
 * A collapsible transcript card for one agent turn: a header summarizing the
 * turn's model, effort, tool/subagent/merge/error chips, and token totals, with
 * an expandable body of the turn's events (subagent child sessions render nested).
 *
 * @param {TurnCardProps} props - The turn, its position, and open/nesting options
 * @returns {JSX.Element} The rendered turn card
 */
export function TurnCard({ turn, index, total, defaultOpen = false, onOpen, childSessions }: TurnCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const events = turn.events ?? [];
  const typeOf = (e: (typeof events)[number]) => (e.event_type ?? '').toUpperCase();
  const toolCalls = events.filter((e) => typeOf(e) === 'TOOL_CALL' || typeOf(e) === 'TOOL_ERROR').length;
  const hasThinking = events.some((e) => typeOf(e) === 'THINKING' || typeOf(e) === 'PLANNING');
  const childOf = (e: (typeof events)[number]) => (e.child_session ? childSessions?.get(e.child_session) : undefined);
  // Errors include a failed child (subagent) session, so a failure is visible on
  // the collapsed parent turn without expanding each subagent.
  const hasError = events.some(
    (e) =>
      typeOf(e) === 'ERROR' || typeOf(e) === 'TOOL_ERROR' || e.status === 'ERROR' || childOf(e)?.status === 'ERROR',
  );
  const subagentCount = events.filter((e) => childOf(e)).length;
  const mergedCount = events.filter((e) => e.wrote_merged_code).length;

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && onOpen) onOpen();
      return next;
    });
  };

  return (
    <Card>
      <Header type="button" open={open} onClick={handleToggle} aria-expanded={open}>
        <Chevron open={open}>▶</Chevron>
        <TurnLabel>
          Turn {index + 1}/{total}
        </TurnLabel>
        {turn.model && <ModelBadge title={turn.model}>{turn.model}</ModelBadge>}
        {turn.effort && <EffortBadge title={`Reasoning effort: ${turn.effort}`}>{turn.effort}</EffortBadge>}
        <ChipRow>
          {toolCalls > 0 && <MiniChip>{toolCalls} tools</MiniChip>}
          {subagentCount > 0 && (
            <MiniChip subagent>
              {subagentCount} subagent{subagentCount > 1 ? 's' : ''}
            </MiniChip>
          )}
          {hasThinking && <MiniChip>thought</MiniChip>}
          {mergedCount > 0 && <MiniChip merged>{mergedCount} merged</MiniChip>}
          {hasError && <MiniChip danger>error</MiniChip>}
        </ChipRow>
        <HeaderMeta>
          {fmtNum(turn.input_tokens ?? 0)} in · {fmtNum(turn.output_tokens ?? 0)} out
          {(turn.cache_read_tokens ?? 0) > 0 && <> · {fmtNum(turn.cache_read_tokens ?? 0)} cache read</>}
          {(turn.cache_creation_tokens ?? 0) > 0 && <> · {fmtNum(turn.cache_creation_tokens ?? 0)} cache write</>}
          {' tokens · '}
          {fmtDuration(turn.duration_ms)}
        </HeaderMeta>
      </Header>
      {open && (
        <Body>
          {events.map((event) => {
            const child = event.child_session ? childSessions?.get(event.child_session) : undefined;
            if (child) {
              return <SubagentSection key={event.id} childSession={child} />;
            }
            return <EventBlock key={event.id} event={event} />;
          })}
        </Body>
      )}
    </Card>
  );
}

/**
 * Renders a subagent child session nested inside its spawning turn: a collapsible
 * card whose body is the child session's own turns (rendered recursively as
 * TurnCards). Collapsed by default to keep the parent transcript scannable.
 */
function SubagentSection({ childSession }: { childSession: AgentMinerChildSession }) {
  const [open, setOpen] = useState(false);
  const turns = childSession.turns ?? [];
  const label = childSession.name || 'subagent';
  const inTok = childSession.total_input_tokens ?? 0;
  const outTok = childSession.total_output_tokens ?? 0;
  const failed = childSession.status === 'ERROR';

  return (
    <SubagentCard failed={failed}>
      <SubagentHeader type="button" open={open} onClick={() => setOpen((prev) => !prev)} aria-expanded={open}>
        <Chevron open={open}>▶</Chevron>
        <SubagentAvatar failed={failed}>⑂</SubagentAvatar>
        <SubagentLabel title={label}>{label}</SubagentLabel>
        <SubagentBadge failed={failed}>{failed ? 'error' : 'subagent'}</SubagentBadge>
        <SubagentMeta>
          {turns.length} turn{turns.length === 1 ? '' : 's'} · {fmtNum(inTok)} in · {fmtNum(outTok)} out ·{' '}
          {fmtDuration(childSession.duration_ms)}
        </SubagentMeta>
      </SubagentHeader>
      {open && (
        <SubagentBody>
          {turns.length === 0 ? (
            <SubagentEmpty>No subagent activity recorded.</SubagentEmpty>
          ) : (
            turns.map((turn, index) => (
              <TurnCard key={turn.id} turn={turn} index={index} total={turns.length} defaultOpen={turns.length === 1} />
            ))
          )}
        </SubagentBody>
      )}
    </SubagentCard>
  );
}

const Card = styled.div`
  border-radius: 8px;
  border: 1px solid ${skyLight};
  background: white;
  overflow: hidden;
`;

const Header = styled.button<{ open: boolean }>`
  width: 100%;
  padding: 10px 12px;
  background: ${(props) => (props.open ? skyLightest : 'white')};
  border: none;
  border-bottom: ${(props) => (props.open ? `1px solid ${skyLight}` : 'none')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
`;

const Chevron = styled.span<{ open: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${inkLight};
  flex-shrink: 0;
  transform: ${(props) => (props.open ? 'rotate(90deg)' : 'none')};
  transition: transform 0.15s ease;
`;

const TurnLabel = styled.span`
  font: 600 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  width: 58px;
  flex-shrink: 0;
`;

const ChipRow = styled.div`
  flex: 1;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const chipBackground = (props: { danger?: boolean; subagent?: boolean; merged?: boolean }): string => {
  if (props.danger) return redLightest;
  if (props.subagent) return secondaryLightest;
  if (props.merged) return purpleLightest;
  return skyLightest;
};

const chipColor = (props: { danger?: boolean; subagent?: boolean; merged?: boolean }): string => {
  if (props.danger) return redDarkest;
  if (props.subagent) return secondaryDarkest;
  if (props.merged) return purpleDarkest;
  return inkDark;
};

const MiniChip = styled.span<{ danger?: boolean; subagent?: boolean; merged?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 99px;
  font: 600 10px/1.3 'Figtree', sans-serif;
  background: ${(props) => chipBackground(props)};
  color: ${(props) => chipColor(props)};
`;

const HeaderMeta = styled.span`
  font: 500 11px/1 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SubagentCard = styled.div<{ failed?: boolean }>`
  border-radius: 8px;
  border: 1px solid ${(props) => (props.failed ? redLightest : secondaryLightest)};
  border-left: 3px solid ${(props) => (props.failed ? redBase : secondaryBase)};
  background: ${(props) => (props.failed ? redLightest : secondaryLightest)}22;
  overflow: hidden;
`;

const SubagentHeader = styled.button<{ open: boolean }>`
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-bottom: ${(props) => (props.open ? `1px solid ${secondaryLightest}` : 'none')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
`;

const SubagentAvatar = styled.span<{ failed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${(props) => (props.failed ? redBase : secondaryDarkest)};
  color: #fff;
  font: 600 11px/1 'Figtree', sans-serif;
  flex-shrink: 0;
`;

const SubagentLabel = styled.span`
  font: 600 12px/1 'Figtree', sans-serif;
  color: ${secondaryDarkest};
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SubagentBadge = styled.span<{ failed?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 99px;
  font: 600 9.5px/1.3 'Figtree', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(props) => (props.failed ? redLightest : secondaryLightest)};
  color: ${(props) => (props.failed ? redDarkest : secondaryDarkest)};
  flex-shrink: 0;
`;

const SubagentMeta = styled.span`
  flex: 1;
  text-align: right;
  font: 500 11px/1 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const SubagentBody = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SubagentEmpty = styled.div`
  font: 400 12px/1.4 'Figtree', sans-serif;
  color: ${inkLight};
`;
